'use client';

import { MetricExplodeDialog, PodDetailDialog, type PodDetailBucket } from '@slims/ui';
import { getMetric } from '@slims/shared/metrics';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMetricExplode } from '@/hooks/use-metric-explode';
import { usePodDetail } from '@/hooks/use-pod-detail';
import { podOptions, usePods } from '@/hooks/use-pods';

/**
 * Il cablaggio dell'esploso per POD, uno per pagina.
 *
 * Ogni pannello — KPI o grafico — dichiara solo la propria metrica e il totale
 * che sta mostrando; questo componente fa il resto: chiede la scomposizione,
 * apre la classifica e, su una riga, scende al dettaglio del POD con produzione e
 * consumo per livello.
 *
 * I **grafici** saltano la classifica: vedi `MetricExplode`.
 *
 * Nessuna presentazione: quella sta in `MetricExplodeDialog` e
 * `PodDetailDialog`. Segue il taglio di `PodExplode`, che era già pensato per
 * agganciarsi a qualunque vista energetica.
 */
export interface MetricExplodeTarget {
	/** Id della metrica nel registro `@slims/shared/metrics`. */
	metricId: string;
	/**
	 * Il totale che il pannello sta mostrando. Serve al piede di quadratura: se
	 * la somma per POD non torna, l'esploso lo dice invece di lasciar credere che
	 * i due numeri siano lo stesso.
	 */
	kpiTotal?: number | null;
	/** Giorno civile iniziale `YYYY-MM-DD`. Default: il mese civile corrente. */
	from?: string;
	/** Giorno civile finale incluso `YYYY-MM-DD`. */
	to?: string;
	/** Periodo già leggibile, es. «luglio 2026». Default: derivato da from/to. */
	periodo?: string;
	/** Categoria di socio (`res` | `com` | `pa`) per i KPI di un solo gruppo. */
	memberGroup?: string;
	/** Socio singolo, per i KPI della sua scheda. */
	memberId?: string;
	/** Configurazione da leggere, dove la pagina ne mostra più di una affiancate. */
	orgId?: string;
}

export interface MetricExplodeProps {
	/** `null` = chiuso. Aprire l'esploso significa impostare un target. */
	target: MetricExplodeTarget | null;
	onClose: () => void;
}

const MONTH_FMT = new Intl.DateTimeFormat('it-IT', { month: 'long', year: 'numeric' });
const DAY_FMT = new Intl.DateTimeFormat('it-IT', {
	weekday: 'long',
	day: 'numeric',
	month: 'long',
	year: 'numeric',
});

/** Mezzogiorno UTC del giorno civile: sta dentro la giornata italiana a ogni fuso. */
function noonOf(day: string): number {
	return Date.parse(`${day}T12:00:00Z`);
}

function shiftDays(day: string, delta: number): string {
	return new Date(noonOf(day) + delta * 86_400_000).toISOString().slice(0, 10);
}

/** Il mese civile corrente, come coppia di giorni con `to` incluso. */
function currentMonth(): { from: string; to: string } {
	const now = new Date();
	const first = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 12);
	const nextMonth = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 12);
	return {
		from: new Date(first).toISOString().slice(0, 10),
		to: new Date(nextMonth - 86_400_000).toISOString().slice(0, 10),
	};
}

/** Giorni civili coperti da un intervallo con `to` incluso. */
function spanDays(from: string, to: string): number {
	return Math.round((noonOf(to) - noonOf(from)) / 86_400_000) + 1;
}

/**
 * Il grano con cui aprire il dettaglio su un intervallo dato.
 *
 * L'intervallo è quello del pannello — cambiare finestra fra un POD e l'altro
 * renderebbe il confronto impossibile — quindi a variare è il grano: un mese al
 * quarto d'ora sarebbe una serie da tremila punti, illeggibile e lenta, e la
 * stessa domanda su un mese si legge sui giorni. Il selettore resta lì per
 * scendere di grano quando serve.
 */
function bucketForSpan(from: string, to: string): PodDetailBucket {
	const days = spanDays(from, to);
	if (days <= 1) return 'slot';
	if (days <= 7) return 'hour';
	if (days <= 120) return 'day';
	return 'month';
}

/** Quanti punti produce un grano in un giorno. */
const POINTS_PER_DAY: Record<PodDetailBucket, number> = { slot: 96, hour: 24, day: 1, month: 1 / 30 };

/**
 * I grani che una finestra **fissa** può mostrare.
 *
 * Dove l'intervallo si può muovere, un grano fuori scala si assorbe cambiandolo
 * — il quarto d'ora su un mese stringe all'ultimo giorno. Dove invece la
 * finestra è quella del pannello da cui si è aperto il grafico, stringerla
 * significherebbe mostrare un intervallo diverso da quello richiesto: allora il
 * grano che non ci sta va tolto dalla scelta, non corretto dopo. Restano fuori
 * sia le serie da migliaia di punti sia quelle che si ridurrebbero a un punto
 * solo (i «Mesi» su un mese).
 */
function bucketsForSpan(from: string, to: string): PodDetailBucket[] {
	const days = spanDays(from, to);
	const fits = (['slot', 'hour', 'day', 'month'] as PodDetailBucket[]).filter((bucket) => {
		const points = days * POINTS_PER_DAY[bucket];
		return points >= 2 && points <= 1500;
	});
	return fits.length > 0 ? fits : [bucketForSpan(from, to)];
}

/** Etichetta del periodo: un giorno solo si dice per esteso, un mese come mese. */
function describePeriod(from: string, to: string): string {
	if (from === to) return DAY_FMT.format(new Date(noonOf(from)));
	const a = new Date(noonOf(from));
	const b = new Date(noonOf(to));
	const sameMonth = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
	if (sameMonth) return MONTH_FMT.format(a);
	return `${MONTH_FMT.format(a)} → ${MONTH_FMT.format(b)}`;
}

export interface PodDetailTarget {
	podId: string;
	/** Giorno civile `YYYY-MM-DD` da cui partire. */
	day: string;
	/** Base di riconciliazione; default la curva giorno. */
	/** Configurazione da leggere, dove la pagina ne mostra più di una affiancate. */
	orgId?: string;
}

/**
 * Il dettaglio di un POD aperto direttamente, senza passare dalla classifica.
 *
 * Serve dove il POD è già stato scelto da chi guarda — la riga di un'anomalia, un
 * misuratore aperto nella scheda del socio — e la domanda non è «quale POD» ma
 * «cosa è successo su questo». Il giorno è pinnato al fatto: un crollo dei
 * consumi e una telemetria caduta e coperta dalla stima si somigliano molto nelle
 * viste aggregate, e qui si distinguono a occhio.
 */
export function PodDetailExplode({
	target,
	onClose,
}: {
	target: PodDetailTarget | null;
	onClose: () => void;
}) {
	const [bucket, setBucket] = useState<PodDetailBucket>('slot');
	const [range, setRange] = useState<{ from: string; to: string } | null>(null);
	// Il POD mostrato può divergere da quello del target: il selettore permette
	// di passare al POD accanto senza chiudere e riaprire il dialogo.
	const [podId, setPodId] = useState<string | null>(target?.podId ?? null);

	// Il target cambia mentre il componente resta montato: senza questo, aprire
	// una seconda riga mostrerebbe il giorno scelto per la prima. Ricalcolo in
	// render e non in effetto, così non c'è un frame con la coppia sbagliata.
	const key = target ? `${target.podId}|${target.day}` : null;
	const [seen, setSeen] = useState<string | null>(key);
	if (key !== seen) {
		setSeen(key);
		if (target) {
			setPodId(target.podId);
			setBucket('slot');
			setRange({ from: target.day, to: target.day });
		}
	}

	const activePodId = podId ?? target?.podId ?? null;
	const pods = usePods({ orgId: target?.orgId ?? null, enabled: target != null });

	const detail = usePodDetail({
		podId: activePodId,
		from: range?.from ?? null,
		to: range?.to ?? null,
		bucket,
		orgId: target?.orgId ?? null,
		enabled: target != null,
	});

	if (!target || !range) return null;
	const today = new Date().toISOString().slice(0, 10);

	return (
		<PodDetailDialog
			open
			onOpenChange={(o) => {
				if (!o) onClose();
			}}
			codice={detail.data?.pod.codice ?? '-'}
			memberName={detail.data?.pod.memberName ?? null}
			cabinaPrimaria={detail.data?.pod.cabinaPrimaria ?? null}
			periodo={describePeriod(range.from, range.to)}
			bucket={bucket}
			sections={detail.data?.directions ?? []}
			loading={detail.isLoading}
			pods={podOptions(pods.data)}
			podId={activePodId ?? undefined}
			onSelectPod={setPodId}
			onBucketChange={(next) => {
				setBucket(next);
				// Allargare il grano senza allargare la finestra darebbe un solo
				// punto: il giorno diventa il mese che lo contiene.
				setRange(
					next === 'slot'
						? { from: target.day, to: target.day }
						: { from: `${target.day.slice(0, 7)}-01`, to: target.day },
				);
			}}
			onPrev={() => {
				const span = Math.round((noonOf(range.to) - noonOf(range.from)) / 86_400_000) + 1;
				setRange({ from: shiftDays(range.from, -span), to: shiftDays(range.to, -span) });
			}}
			onNext={() => {
				const span = Math.round((noonOf(range.to) - noonOf(range.from)) / 86_400_000) + 1;
				setRange({ from: shiftDays(range.from, span), to: shiftDays(range.to, span) });
			}}
			nextDisabled={range.to >= today}
		/>
	);
}

export function MetricExplode({ target, onClose }: MetricExplodeProps) {
	const [podId, setPodId] = useState<string | null>(null);
	const [bucket, setBucket] = useState<PodDetailBucket>('slot');
	/** Finestra del dettaglio: parte da quella dell'esploso e poi la si naviga. */
	const [detailRange, setDetailRange] = useState<{ from: string; to: string } | null>(null);

	const range = useMemo(() => {
		if (target?.from && target?.to) return { from: target.from, to: target.to };
		return currentMonth();
	}, [target?.from, target?.to]);

	/**
	 * Una metrica-serie è un grafico, e un grafico non si esplode in una
	 * classifica: davanti a una curva la domanda non è «quale POD ha il numero più
	 * grosso» ma «com'è fatta questa curva su un POD». Per queste metriche
	 * l'esploso salta la classifica e apre direttamente il dettaglio — la stessa
	 * curva del pannello, rifatta POD per POD e spezzata per livello, con la
	 * tendina in cima per scegliere quale POD guardare.
	 *
	 * La distinzione la fa il registro condiviso (`compute.kind`), non il pannello
	 * che chiama: è lì che una metrica dichiara di essere una serie.
	 */
	const isSeries = getMetric(target?.metricId ?? '')?.compute.kind === 'energy_series';

	const breakdown = useMetricExplode({
		metricId: target?.metricId ?? null,
		from: range.from,
		to: range.to,
		memberGroup: target?.memberGroup ?? null,
		memberId: target?.memberId ?? null,
		orgId: target?.orgId ?? null,
		enabled: target != null,
	});

	// Il dettaglio si apre sulla **stessa finestra** della classifica: è la
	// condizione perché i POD si possano confrontare fra loro passando dall'uno
	// all'altro. A cambiare è il grano, che scende al quarto d'ora solo dove la
	// finestra è un giorno (vedi `bucketForSpan`).
	const detail = usePodDetail({
		podId,
		from: detailRange?.from ?? null,
		to: detailRange?.to ?? null,
		bucket,
		orgId: target?.orgId ?? null,
		enabled: podId != null,
	});

	const openPod = useCallback(
		(id: string) => {
			setPodId(id);
			setBucket(bucketForSpan(range.from, range.to));
			setDetailRange(range);
		},
		[range],
	);

	const changeBucket = useCallback(
		(next: PodDetailBucket) => {
			setBucket(next);
			// Su una serie l'intervallo è quello del pannello e non si tocca: qui si
			// cambia solo il grano, e i grani che in quella finestra non ci stanno
			// non vengono nemmeno offerti (`bucketsForSpan`).
			if (isSeries) return;
			// Il quarto d'ora è l'unico grano che la finestra non regge: su un mese
			// sarebbero tremila punti. Lì si stringe all'ultimo giorno, e dalle
			// frecce si scorrono i giorni precedenti.
			setDetailRange(next === 'slot' ? { from: range.to, to: range.to } : range);
		},
		[isSeries, range],
	);

	const shiftDetail = useCallback(
		(delta: number) => {
			setDetailRange((cur) => {
				if (!cur) return cur;
				const span = Math.round((noonOf(cur.to) - noonOf(cur.from)) / 86_400_000) + 1;
				return {
					from: shiftDays(cur.from, delta * span),
					to: shiftDays(cur.to, delta * span),
				};
			});
		},
		[],
	);

	// Il target cambia mentre il componente resta montato: senza questo, il
	// secondo esploso si aprirebbe sul POD e sulla finestra scelti nel primo.
	// Ricalcolo in render e non in effetto, così non c'è un frame con la coppia
	// sbagliata.
	const targetKey = target
		? `${target.metricId}|${range.from}|${range.to}|${target.memberId ?? ''}|${target.orgId ?? ''}`
		: null;
	const [seenTarget, setSeenTarget] = useState<string | null>(targetKey);
	if (targetKey !== seenTarget) {
		setSeenTarget(targetKey);
		setPodId(null);
		setDetailRange(null);
		setBucket(bucketForSpan(range.from, range.to));
	}

	const rows = breakdown.data?.rows ?? [];
	// Il POD di partenza di una serie è il primo della classifica: anche per un
	// grafico le righe tornano ordinate per kWh della finestra, quindi si entra da
	// quello che pesa di più e col selettore si scorre il resto.
	const firstPodId = rows[0]?.podId ?? null;
	useEffect(() => {
		if (target == null || !isSeries || podId != null || firstPodId == null) return;
		openPod(firstPodId);
	}, [target, isSeries, podId, firstPodId, openPod]);

	if (!target) return null;

	const metric = breakdown.data?.metric;
	const today = new Date().toISOString().slice(0, 10);
	// Sulle serie il dettaglio si apre subito, prima ancora che la scomposizione
	// dica quale POD mostrare: l'attesa sta dentro al dialogo invece di lasciare
	// il click senza risposta per il tempo della fetch.
	const detailOpen = isSeries || (podId != null && detailRange != null);
	const shownRange = detailRange ?? range;
	// Finché il dettaglio non ha risposto, intestazione e tendina si reggono sulla
	// riga della classifica: il codice POD c'è già lì, e mostrarlo evita che il
	// dialogo si apra su un «socio non assegnato» che poi cambia da solo.
	const activeRow = podId != null ? (rows.find((r) => r.podId === podId) ?? null) : null;

	return (
		<>
			{!isSeries && (
				<MetricExplodeDialog
					open={podId == null}
					onOpenChange={(o) => {
						if (!o) onClose();
					}}
					label={metric?.label ?? target.metricId}
					unit={metric?.unit ?? 'kWh'}
					periodo={target.periodo ?? describePeriod(range.from, range.to)}
					decomposition={metric?.decomposition === 'allocated' ? 'allocated' : 'sum'}
					rows={rows}
					total={breakdown.data?.total ?? 0}
					kpiTotal={target.kpiTotal ?? null}
					allocationBasis={breakdown.data?.allocationBasis ?? metric?.allocationBasis}
					note={metric?.note}
					loading={breakdown.isLoading}
					emptyReason={breakdown.error ?? breakdown.data?.emptyReason}
					layerFilterEnabled={breakdown.data?.layerFilter?.enabled ?? true}
					layerFilterReason={breakdown.data?.layerFilter?.reason}
					onSelectPod={openPod}
				/>
			)}

			{detailOpen && (
				<PodDetailDialog
					open
					onOpenChange={(o) => {
						if (o) return;
						// Dalla classifica il dettaglio è una discesa, e si risale di un
						// passo; su una serie la classifica non c'è mai stata, quindi
						// chiudere il dettaglio chiude l'esploso.
						if (isSeries) onClose();
						setPodId(null);
					}}
					codice={detail.data?.pod.codice ?? activeRow?.codice ?? '-'}
					memberName={detail.data?.pod.memberName ?? activeRow?.memberName ?? null}
					cabinaPrimaria={detail.data?.pod.cabinaPrimaria ?? null}
					periodo={describePeriod(shownRange.from, shownRange.to)}
					bucket={bucket}
					sections={detail.data?.directions ?? []}
					// Senza POD scelto si sta ancora aspettando: o la scomposizione, o il
					// frame in cui il primo POD viene aperto. Il vuoto va mostrato solo
					// quando è una risposta — cioè quando di POD non ce n'è nessuno.
					loading={
						detail.isLoading || (podId == null && (breakdown.isLoading || firstPodId != null))
					}
					// La lista è quella della classifica, nello stesso ordine: passare al
					// POD accanto è scendere di una riga senza risalire al dialogo.
					pods={rows.map((r) => ({
						podId: r.podId,
						codice: r.codice,
						memberName: r.memberName,
					}))}
					podId={podId ?? undefined}
					onSelectPod={setPodId}
					onBucketChange={changeBucket}
					// Sulle serie la finestra è quella del grafico da cui si è aperto:
					// niente frecce per scorrerla, e niente grani che per starci
					// dentro dovrebbero stringerla.
					buckets={isSeries ? bucketsForSpan(range.from, range.to) : undefined}
					onPrev={isSeries ? undefined : () => shiftDetail(-1)}
					onNext={isSeries ? undefined : () => shiftDetail(1)}
					nextDisabled={shownRange.to >= today}
				/>
			)}
		</>
	);
}
